import { useMemo } from 'react';
import {
  Direction,
  EngagementFilterType,
  MessageContactType,
  MessagingROIType,
  MessagingTableViewData,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { capitalize } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { Chip, ChipProps, TableColumnConfig } from '@frontend/design-system';
import { LocationChip } from '../../components/location-chip';
import { messagingTableLabelMap, roiTypeLabels } from '../../components/messaging-roi/messaging-roi-config';
import { UserCard } from '../../components/user-card';
import { dateTimeCellRenderer, phoneNumberCellRenderer } from '../../utils/cell-renderers';

/** Columns that can be optionally hidden (e.g. when the table is scoped to a single ROI type). */
export type MessagingTableColumnId = 'messageType' | 'smsDirection' | 'contactType';

/**
 * Maps MessageContactType enum values to Chip color variants.
 */
const MESSAGE_CONTACT_TYPE_VARIANT_MAP: Record<string, ChipProps['variant']> = {
  [MessageContactType.MESSAGES_EXISTING_PATIENTS]: 'primary',
  [MessageContactType.MESSAGES_UNKNOWN_PATIENTS]: 'neutral',
};

const getMessageTableColumns = ({
  t,
  hiddenColumns,
}: {
  t: (key: string) => string;
  hiddenColumns: MessagingTableColumnId[];
}): TableColumnConfig<MessagingTableViewData>[] => {
  const columns: TableColumnConfig<MessagingTableViewData>[] = [
    {
      id: 'contactName',
      Header: t('Contact Name'),
      accessor: (item) => item,
      disableColumnFilter: true,
      cellRenderer: (value: MessagingTableViewData) => {
        return (
          <UserCard
            variant='compact'
            lastName={value?.contactName}
            key={value?.contactName}
            openProfileOnClick
            phoneNumber={value?.contactNumber}
            showOnlyName
            userId={value?.patientId}
            locationId={value?.locationId}
          />
        );
      },
      disableSortBy: true,
      minWidth: 220,
      sticky: 'left',
    },
    {
      id: 'contactNumber',
      Header: t('Contact Number'),
      accessor: 'contactNumber',
      cellRenderer: phoneNumberCellRenderer,
      minWidth: 160,
    },
    {
      id: 'locationName',
      Header: t('Location'),
      accessor: 'locationName',
      cellRenderer: (value: string) => (value ? <LocationChip locationName={value} maxWidth={170} /> : '-'),
    },
    {
      id: 'messageDate',
      Header: t('Time of Message'),
      accessor: 'messageDate',
      cellRenderer: dateTimeCellRenderer({ smart: true }),
      minWidth: 180,
    },
    {
      id: 'engaged',
      Header: t('Engaged'),
      accessor: 'engaged',
      cellRenderer: (value: EngagementFilterType) => {
        return messagingTableLabelMap[value] ?? '-';
      },
      width: 120,
    },
    {
      id: 'contactType',
      Header: t('Contact Type'),
      accessor: 'contactType',
      cellRenderer: (value: MessageContactType) => {
        if (!value) return '-';
        const label = messagingTableLabelMap[value];
        if (!label) return '-';
        const variant = MESSAGE_CONTACT_TYPE_VARIANT_MAP[value] ?? 'neutral';
        return (
          <Chip maxWidth='100%' variant={variant}>
            {t(label)}
          </Chip>
        );
      },
    },
    {
      id: 'messageType',
      Header: t('Message Type'),
      accessor: 'messageType',
      cellRenderer: (value: string) => {
        if (!value) return '-';
        return roiTypeLabels[value as MessagingROIType] || capitalize(value);
      },
      minWidth: 180,
    },
    {
      id: 'smsDirection',
      Header: t('Message Direction'),
      accessor: 'smsDirection',
      cellRenderer: (value: string) => {
        if (!value) return '-';
        return messagingTableLabelMap[value as Direction] || capitalize(value);
      },
      minWidth: 180,
    },
  ];

  const visibleColumns: TableColumnConfig<MessagingTableViewData>[] = [];
  for (const column of columns) {
    if (!hiddenColumns.includes(column.id as MessagingTableColumnId)) {
      visibleColumns.push(column);
    }
  }

  return visibleColumns;
};

/**
 * Returns table column configuration for the messaging analytics table.
 *
 * @param hiddenColumns - optional column ids to omit (e.g. `messageType` + `smsDirection` when the
 * table is scoped to a single, outbound ROI type such as bulk text).
 */
export const useMessagingTableColumns = ({
  hiddenColumns = [],
}: { hiddenColumns?: MessagingTableColumnId[] } = {}): TableColumnConfig<MessagingTableViewData>[] => {
  const { t } = useTranslation('analytics');
  // Key on contents so inline arrays from callers don't bust the memo every render.
  const hiddenColumnsKey = hiddenColumns.join(',');

  return useMemo(
    () => getMessageTableColumns({ t, hiddenColumns: hiddenColumnsKey.split(',') as MessagingTableColumnId[] }),
    [t, hiddenColumnsKey]
  );
};
